import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, CardText, CardTitle } from "reactstrap";
import { Check } from "lucide-react";
import { toast } from "react-toastify";
import { getAllSubscriptionPlans, subscribeToPLan as initiatePayment } from "../../../api-handle/merchantSubscriptionApi";
import Loader from '../../../CommonElements/Loader/loader';
import BuyNowModal from './BuyNowModal';
import "./SubscriptionCards.scss";

const SubscriptionCards = ({ currentPlanId, currentPlanPrice, currentPlan, plan }) => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchSubscriptionPlans = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await getAllSubscriptionPlans();
            setSubscriptions(res.plans);
        } catch (err) {
            setError(err.message || "Failed to fetch subscription plans");
        }
        setLoading(false);
    };

    useEffect(() => {
        if (plan) setSubscriptions(plan);
        else fetchSubscriptionPlans();
    }, [plan]);

    const isValidPaymentUrl = (url) =>
        typeof url === "string" && /^https?:\/\//i.test(url.trim());

    const handleBuyNow = (plan) => {
        // Pending payment - open modal so user re-confirms payment method
        // and backend generates a fresh paymentUrl (old one may be expired/invalid)
        if (currentPlan?.paymentStatus === "pending" && plan._id === currentPlanId) {
            setSelectedPlan(plan);
            setModalOpen(true);
            return;
        }

        // Already purchased
        if (currentPlanId && plan._id === currentPlanId) {
            toast.info("This is your current plan.");
            return;
        }

        // Downgrade check
        if (currentPlanPrice && plan.price < currentPlanPrice) {
            toast.warning("You can only upgrade to a higher plan.");
            return;
        }

        // Open modal for new payment
        setSelectedPlan(plan);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedPlan(null);
    };

    const handleSubscribePlan = async (plan, paymentMethodId) => {
        let res;
        try {
            res = await initiatePayment({ planId: plan._id, paymentMethodId });
        } catch (error) {
            const msg = error?.message || "Failed to initiate payment";
            toast.error(msg);
            throw new Error(msg);
        }

        if (!res?.isSuccess) {
            const msg = res?.message || "Failed to get payment link";
            toast.error(msg);
            throw new Error(msg);
        }

        if (!isValidPaymentUrl(res.paymentUrl)) {
            const msg = "Invalid payment URL received. Please try again.";
            toast.error(msg);
            throw new Error(msg);
        }

        if (res.invoiceId) {
            localStorage.setItem("invoiceId", res.invoiceId);
        }
        window.location.assign(res.paymentUrl.trim());
    };

    const getButtonProps = (plan) => {
        if (currentPlan?.paymentStatus === "pending" && plan._id === currentPlanId)
            return { text: "Complete Payment", disabled: false, className: "pending-btn" };
        if (currentPlan && plan._id !== currentPlan.subscriptionPlan._id) {
            return { text: "Buy Now", disabled: true, className: "purchased-btn" };
        }
        if (currentPlanId && plan._id === currentPlanId)
            return { text: "Purchased", disabled: true, className: "purchased-btn" };
        if (currentPlanPrice && plan.price > currentPlanPrice)
            return { text: "Upgrade Plan", disabled: false, className: "upgrade-btn" };
        return { text: "Buy Now", disabled: false, className: "buy-now-btn" };
    };

    return (
        <div className="subscription-page" style={{ cursor: "pointer" }}>
            {error && <div className="alert alert-danger">{error}</div>}
            {loading ? <Loader /> : (
                <div className="subscription-container-02">
                    {subscriptions.map(plan => {
                        const { text, disabled, className } = getButtonProps(plan);
                        console.log(plan);
                        return (
                            <div
                                key={plan._id}
                                style={{
                                    position: "relative",
                                    background: "#ffffff",
                                    borderRadius: "18px",
                                    padding: "28px",
                                    boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                                    overflow: "hidden",
                                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    maxWidth: "450px",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-6px)";
                                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.15)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.1)";
                                }}
                            >
                                {/* Gradient Overlay */}
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        background: "linear-gradient(135deg, rgba(51,189,191,0.15), transparent)",
                                        zIndex: 0,
                                    }}
                                />

                                {/* Content */}
                                <div style={{ position: "relative", zIndex: 1 }}>
                                    <h3
                                        style={{
                                            fontSize: "22px",
                                            fontWeight: 700,
                                            marginBottom: "12px",
                                            color: "#2d3436",
                                        }}
                                    >
                                        {plan.title}
                                    </h3>

                                    <div
                                        style={{
                                            fontSize: "34px",
                                            fontWeight: 800,
                                            color: "#33bdbf",
                                            marginBottom: "8px",
                                        }}
                                    >
                                        KWD {plan.price}
                                        <span
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                color: "#666",
                                                marginLeft: "6px",
                                            }}
                                        >
                                            per {plan.duration} {plan.durationType === "monthly" ? "Month" : "Year"}
                                        </span>
                                    </div>

                                    <p
                                        style={{
                                            fontSize: "15px",
                                            color: "#555",
                                            lineHeight: 1.6,
                                            marginBottom: "20px",
                                        }}
                                    >
                                        {plan.description}
                                    </p>

                                    <ul
                                        style={{
                                            listStyle: "none",
                                            padding: 0,
                                            margin: "16px 0 0",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "12px",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        {plan.benefits.map((benefit, i) => (
                                            <li
                                                key={i}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: "10px",
                                                    color: "#444",
                                                    fontSize: "15px",
                                                    lineHeight: "1.5",
                                                }}
                                            >
                                                <Check
                                                    size={18}
                                                    style={{
                                                        color: "#33bdbf",
                                                        marginTop: "3px",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <span style={{ flex: 1 }}>{benefit}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        disabled={disabled}
                                        onClick={() => handleBuyNow(plan)}
                                        style={{
                                            width: "100%",
                                            padding: "14px",
                                            borderRadius: "12px",
                                            border: "none",
                                            fontSize: "15px",
                                            fontWeight: 600,
                                            cursor: disabled ? "not-allowed" : "pointer",
                                            background: disabled ? "#dfe6e9" : "#33bdbf",
                                            color: disabled ? "#888" : "#ffffff",
                                            transition: "background 0.3s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!disabled) e.currentTarget.style.background = "#2aa7a9";
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!disabled) e.currentTarget.style.background = "#33bdbf";
                                        }}
                                    >
                                        {text}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {selectedPlan && (
                <BuyNowModal
                    isOpen={modalOpen}
                    toggle={closeModal}
                    selectedPlan={selectedPlan}
                    onProceedCheckout={handleSubscribePlan}
                />
            )}
        </div>
    );
};

export default SubscriptionCards;
